import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { WebSocketService } from '@qtek/core/websockets-core';
import { ISchemasEnum } from '@qtek/shared/models';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { SchemaEditorActions } from './schemas.actions';
import { selectProductSchemaTypes } from './schemas.reducer';
import { SchemasService } from './schemas.service';

@Injectable()
export class SchemasEditorEffects {
  private actions$ = inject(Actions);
  private schemasService = inject(SchemasService);
  private webSocketService = inject(WebSocketService);
  private store = inject(Store);

  connectDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchemaEditorActions.connectSchemas),
      switchMap(({ mysid }: { mysid: string }) =>
        this.schemasService
          .subscribe({ mysid })
          .pipe(
            takeUntil(
              this.actions$.pipe(ofType(SchemaEditorActions.disconnectSchemas))
            )
          )
      ),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? SchemaEditorActions.getSchemasSuccess({ schemas: res })
              : SchemaEditorActions.getSchemasFailure();

          case 'upd':
            return res
              ? SchemaEditorActions.updateSchemaSuccess({ schema: res })
              : SchemaEditorActions.updateSchemaFailure({ error: sts });
          default:
            return {
              type: '[Schema] Unknown operation',
            };
        }
      })
    )
  );

  updateSchema$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SchemaEditorActions.updateSchema),
        tap(({ payload }) => {
          this.schemasService.updateAttribute(payload);
        })
      ),
    { dispatch: false }
  );

  private readonly pendingSids = new Set<string>();

  getSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchemaEditorActions.getSchema),
      withLatestFrom(this.store.select(selectProductSchemaTypes)),
      filter(
        ([{ sid }, schemas]) => !schemas[sid] && !this.pendingSids.has(sid)
      ),
      mergeMap(([{ sid }]) => {
        this.pendingSids.add(sid);
        return this.schemasService.getSchemaById(sid).pipe(
          map(({ res }) => {
            let schema: ISchemasEnum;
            try {
              schema = JSON.parse(res);
            } catch (e) {
              console.error("[qtek] Error parsing schema's response: ", e);
              return SchemaEditorActions.getSchemaFailure({ sid, error: e });
            }

            return SchemaEditorActions.getSchemaSuccess({
              schema,
            });
          }),
          catchError(({ sts }) =>
            of(SchemaEditorActions.getSchemaFailure({ sid, error: sts }))
          ),
          finalize(() => this.pendingSids.delete(sid))
        );
      })
    )
  );
}
